import type { FileUIPart } from 'ai';
import { v4 } from 'uuid';
import { coco } from '@cotera/api';
import type { TenantedClient } from '@cotera/client/app/etc/api/typed-client';

/** Whether an attachment should become a chat dataset rather than a file part. */
export const isTabularFilePart = coco.isTabularFilePart;

type DatasetClient = TenantedClient['resource']['dataset'];

/**
 * A composer attachment as it reaches the chat-upload path.
 *
 * `blobSource` is the original `File` the user attached. The composer carries it
 * alongside the serialized `data:` URL so we can upload the real bytes without
 * re-`fetch`ing the data URL — a `fetch(data:…)` the production CSP blocks
 * (`connect-src` has no `data:`), which is what surfaced as "Failed to fetch".
 * It is only present while the part lives in memory and must be stripped before
 * a part is persisted or sent to the server.
 *
 * `datasetId` marks a *receipt*: a placeholder left in place of a tabular file
 * that was already uploaded on an earlier pass (the new-chat → chat-page replay
 * through `sessionStorage`). It carries no bytes, so a later pass neither
 * re-uploads it nor inlines it — but it keeps the message/pending submission
 * non-empty so a CSV-only first message still counts as content and runs a turn.
 *
 * The receipt's `datasetId` is a client-only marker and is never sent to or
 * trusted by the server: the dataset was created org-owned from the
 * authenticated request (`createPendingDataset` stamps `ownerOrgId` from
 * `auth.requireOrgId()`), and the chat turn discovers its pending uploads by
 * scanning server-created `chat_datasets` rows for its own `chat_id`
 * (`resolvePendingChatUploads`), not from this field. A forged receipt is inert
 * — it can neither reference nor attach a dataset the caller does not own.
 */
export type ChatUploadPart = FileUIPart & {
  blobSource?: File;
  datasetId?: string;
  /**
   * A *receipt* for a non-tabular document already uploaded into the chat's
   * ingot on an earlier pass (the new-chat → chat-page replay). Like
   * {@link datasetId} it carries no bytes and is client-only — the server drives
   * the placeholder off its own `chat_files` row, not this field.
   */
  chatFileId?: string;
};

/** Drop the in-memory `File` so a part is safe to persist or send. */
const withoutBlobSource = ({
  blobSource: _blobSource,
  ...rest
}: ChatUploadPart): ChatUploadPart => rest;

/**
 * The real `File` behind an attachment. It rides on `blobSource` from the moment
 * the composer builds the attachment, so no data-URL round-trip is needed.
 */
const fileFromPart = (part: ChatUploadPart): File => {
  if (part.blobSource instanceof File) {
    return part.blobSource;
  }
  throw new Error(
    `Could not read the file data for ${part.filename ?? 'the attachment'}`
  );
};

export type PreparedChatUploads = {
  /**
   * Non-tabular attachments to inline into the outgoing message. Tabular files
   * became datasets and already-uploaded receipts are dropped.
   */
  inlineParts: FileUIPart[];
  /**
   * What to persist in a pending submission (the new-chat → chat-page hop): the
   * inline parts plus a lightweight receipt for each uploaded dataset. Receipts
   * carry no bytes but keep the pending submission non-empty, so a CSV-only
   * first message still counts as content and runs a turn on the chat page.
   */
  pendingParts: ChatUploadPart[];
};

/**
 * Uploads any tabular attachments into chat-scoped datasets, reusing the
 * ordinary CSV upload transport (create-pending → import-raw). `sourceChatId`
 * registers the `chat_datasets` association, so the dataset is hidden from the
 * library and the chat turn knows to post its in-progress placeholder. It does
 * NOT wait for the import — the server turn stops-and-waits for that.
 *
 * The upload runs wherever the real `File` still lives in memory: on the
 * new-chat landing page (before the submission is serialized into
 * `sessionStorage`, which would drop the bytes) and inside the existing-chat
 * submit. Both go through here, so there is a single upload path.
 *
 * Returns two projections of the same pass: `inlineParts` for the message and
 * `pendingParts` for a pending submission (see {@link PreparedChatUploads}).
 */
export const prepareChatUploads = async (
  datasetClient: DatasetClient,
  chatId: string,
  files: ChatUploadPart[]
): Promise<PreparedChatUploads> => {
  const inlineParts: FileUIPart[] = [];
  const pendingParts: ChatUploadPart[] = [];
  for (const part of files) {
    // A receipt from an earlier pass: the file was already uploaded, so keep it
    // (so content gates still fire) but never inline or re-upload it.
    if (
      (typeof part.datasetId === 'string' && part.datasetId !== '') ||
      (typeof part.chatFileId === 'string' && part.chatFileId !== '')
    ) {
      pendingParts.push(withoutBlobSource(part));
      continue;
    }
    if (!isTabularFilePart(part)) {
      // A non-tabular document Ingot can parse goes to the chat's ingot — its
      // bytes never enter the model context. A `coco/upload-file` placeholder is
      // posted server-side; the model reads it via `coco/ingot-query`.
      if (coco.isIngotDocumentFilePart(part)) {
        const id = v4();
        const file = fileFromPart(part);
        const mediaType =
          coco.ingotMediaTypeForPart(part) ??
          (file.type !== '' ? file.type : 'application/octet-stream');
        const uploaded = await datasetClient.uploadChatFile({
          id,
          chatId,
          filename: file.name,
          mediaType,
          file,
        });
        if (uploaded.isErr()) {
          throw new Error(`Could not upload ${file.name}`);
        }
        pendingParts.push({
          type: 'file',
          url: '',
          mediaType: part.mediaType,
          filename: part.filename,
          chatFileId: id,
        });
        continue;
      }
      const clean = withoutBlobSource(part);
      inlineParts.push(clean);
      pendingParts.push(clean);
      continue;
    }
    const id = v4();
    const file = fileFromPart(part);
    const pending = await datasetClient.createCsvPending({
      id,
      sourceChatId: chatId,
      originalFilename: file.name,
      mimeType: file.type !== '' ? file.type : undefined,
      sizeBytes: file.size,
    });
    if (pending.isErr()) {
      throw new Error(`Could not create a dataset for ${file.name}`);
    }
    const uploaded = await datasetClient.uploadCsvBytes({ id, file });
    if (uploaded.isErr()) {
      throw new Error(`Could not upload ${file.name}`);
    }
    pendingParts.push({
      type: 'file',
      url: '',
      mediaType: part.mediaType,
      filename: part.filename,
      datasetId: id,
    });
  }
  return { inlineParts, pendingParts };
};
